import * as amplitude from "@amplitude/analytics-react-native"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { useCorrectionPolling } from "../../../engine/activity/correction/useCorrectionPolling"
import { theme } from "@common/theme/theme"
import { apiRequest } from "@data/redux/actions/api"
import { setFocusedMode, setMiniMode } from "@data/redux/actions/liveMode"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { connect } from "react-redux"
import { activitiesService } from "../../../data/services/activitiesService"
import ActivityDetailsModal from "../read/ActivityDetailsModal"
import { deriveCorrectionFlow, submittedWorkImages } from "../../../engine/activity/correction/correctionFlow"
import CorrectionWait from "./CorrectionWait"

// La correction prête s'annonce un instant avant de s'afficher, le temps que
// l'élève lève les yeux de son défi.
const READY_BEAT = 1300
const LIVE_FOOTER_HEIGHT = 70

/**
 * Entre l'envoi d'une copie que Sam corrige et l'écran de fin d'activité :
 * l'attente, puis la correction.
 *
 * Elle se pose sur l'écran d'activité, comme un checkpoint de Sam — pas sur un
 * écran à part. En live, elle laisse donc la barre du bas à l'élève et la
 * vignette de son tuteur au-dessus d'elle : il reste dans sa séance tout le
 * temps qu'il attend et qu'il lit. Toutes ses sorties mènent au même endroit,
 * l'écran de fin habituel.
 */
const CorrectionSequence = (props) => {
    const { activity, correctionWait, onDone } = props
    const insets = useSafeAreaInsets()

    // Comme pour un checkpoint : la visio passe en vignette, au-dessus.
    useEffect(() => {
        if (!props.isLiveMode) return
        props.setMiniMode(true)
        props.setFocusedMode(false)
    }, [])

    const [correction, setCorrection] = useState({
        reviewStatus: activity.status ?? null,
        status: activity.correction_status ?? null,
        image: activity.correction_image ?? null,
        points: activity.correction_points ?? null,
        workImages: null,
        timedOut: false,
    })

    const readCopy = useCallback(
        (service) =>
            props.apiRequest(service, {}, {}, { with_logs: 1, activity_ids: activity.id }),
        [activity.id]
    )

    useApiStatus(activitiesService.pollActivityCorrection, null, false, (data) => {
        const fresh = Array.isArray(data) ? data.find((a) => a?.id === activity.id) : null
        if (!fresh) return
        setCorrection((c) => {
            const reviewStatus = fresh.status ?? c.reviewStatus
            const status = fresh.correction_status ?? c.status
            // Une correction prête le reste, et un relevé qui n'apprend rien
            // ne redessine rien.
            if (c.status === "completed" || (reviewStatus === c.reviewStatus && status === c.status)) {
                return c
            }
            const freshWorkImages = submittedWorkImages(fresh)
            return {
                ...c,
                reviewStatus,
                status,
                image: fresh.correction_image ?? c.image,
                points: fresh.correction_points ?? c.points,
                workImages: freshWorkImages.length ? freshWorkImages : c.workImages,
            }
        })
    })

    const { awaiting, completed, endingText } = deriveCorrectionFlow({
        reviewStatus: correction.reviewStatus,
        correctionStatus: correction.status,
        timedOut: correction.timedOut,
    })

    const [reading, setReading] = useState(false)
    useEffect(() => {
        if (!completed) return
        const id = setTimeout(() => setReading(true), READY_BEAT)
        return () => clearTimeout(id)
    }, [completed])

    // La part des attentes qui aboutissent à une correction vue : c'est ce que
    // suit l'entrée `correction_in_flow` du catalogue de monitoring.
    useEffect(() => {
        amplitude.track("correction.wait.started", { activity_id: activity.id })
    }, [])
    useEffect(() => {
        if (reading) amplitude.track("correction.viewed", { activity_id: activity.id })
    }, [reading])

    // Les relevés de l'attente ne passent pas par les données du store ; elles
    // se remettent à jour une fois, quand l'attente prend fin, pour que
    // l'activité y porte sa correction.
    const wasAwaiting = useRef(false)
    useEffect(() => {
        if (wasAwaiting.current && !awaiting) {
            readCopy(activitiesService.getActivities)
        }
        wasAwaiting.current = awaiting
    }, [awaiting, readCopy])

    // Un relevé toutes les 5 s tant qu'on attend ; après le dernier, on le dit.
    useCorrectionPolling(
        awaiting,
        () => readCopy(activitiesService.pollActivityCorrection),
        () => setCorrection((c) => ({ ...c, timedOut: true }))
    )

    return (
        <View
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                // En live, la barre du bas reste à l'élève.
                bottom: props.isLiveMode ? LIVE_FOOTER_HEIGHT + insets.bottom : 0,
                paddingTop: insets.top,
                paddingBottom: props.isLiveMode ? 0 : insets.bottom,
                backgroundColor: theme.colors.white,
                // Sous la vignette de la visio (10), comme le checkpoint de Sam.
                zIndex: 8,
                elevation: 8,
            }}
        >
            {reading ? (
                <View
                    style={{
                        flex: 1,
                        alignSelf: "center",
                        width: "100%",
                        maxWidth: 760,
                        paddingTop: 8,
                    }}
                >
                    <ActivityDetailsModal
                        inline
                        visible
                        activity={activity}
                        onClose={onDone}
                        onContinue={onDone}
                        images={correction.workImages ?? submittedWorkImages(activity)}
                        correctionStatus={correction.status}
                        correctionImage={correction.image}
                        correctionPoints={correction.points}
                        noLaunchButton
                    />
                </View>
            ) : (
                <CorrectionWait
                    ready={completed}
                    endingText={endingText}
                    questions={correctionWait?.questions}
                    activityId={activity.id}
                    onSkip={onDone}
                />
            )}
        </View>
    )
}

const mapStateToProps = (state) => ({
    isLiveMode: state.liveMode?.isLiveModeEnabled,
})

const mapDispatchToProps = {
    apiRequest,
    setMiniMode,
    setFocusedMode,
}

export default connect(mapStateToProps, mapDispatchToProps)(CorrectionSequence)
